import { DocumentNode, gql, useMutation, useQuery } from '@apollo/client';
import {
  Avatar,
  Box,
  IconButton,
  Link,
  Stack,
  Tag,
  Td,
  Text,
  Tr,
  useToast,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { EUserGroupRole } from '@/client/legacy-shared-modules/user-group-link/user-group-role';
import { prettyPrintEnum } from '@/client/legacy-shared-modules/utils/prettyPrintEnum';
import { useGroupPermissions } from '@/hooks/useGroupPermissions';
import { logError } from '@/imports/logging/ClientLogger';
import { XIcon } from '@/imports/ui/chakra/feather';
import { READABLE_USER_ROLE_FOR_GROUP } from '@/utilities';

import { GroupMembersPaneMember_UserFragment } from './graphql-types/GroupMembersPaneMember_UserFragment';
import {
  Member_DeleteUserGroupLink,
  Member_DeleteUserGroupLinkVariables,
} from './graphql-types/Member_DeleteUserGroupLink';
import {
  Member_UpdateUserGroupLink,
  Member_UpdateUserGroupLinkVariables,
} from './graphql-types/Member_UpdateUserGroupLink';

import { GroupRoleSelect } from './components/GroupRoleSelect';

const UPDATE_USER_GROUP_LINK = gql`
  mutation Member_UpdateUserGroupLink($input: UpdateUserGroupLinkInput!) {
    updateUserGroupLink(input: $input) {
      data {
        _id
        userId
        groupId
        userGroupRole
      }
    }
  }
`;

const DELETE_USER_GROUP_LINK = gql`
  mutation Member_DeleteUserGroupLink($input: DeleteUserGroupLinkInput!) {
    deleteUserGroupLink(input: $input) {
      data {
        _id
        userId
        groupId
        userGroupRole
      }
    }
  }
`;

export const Member: React.FC<{
  member: GroupMembersPaneMember_UserFragment;
  groupId: string;
  getUserLink: (userId: string) => string;
}> & { userFragment: DocumentNode } = ({ member, groupId, getUserLink }) => {
  const toast = useToast();

  const [updateUserGroupLink, updateUserGroupLinkResult] = useMutation<
    Member_UpdateUserGroupLink,
    Member_UpdateUserGroupLinkVariables
  >(UPDATE_USER_GROUP_LINK);
  const [deleteUserGroupLink, deleteUserGroupLinkResult] = useMutation<
    Member_DeleteUserGroupLink,
    Member_DeleteUserGroupLinkVariables
  >(DELETE_USER_GROUP_LINK, {
    refetchQueries: [
      'GroupMembers_GroupUsersSearch',
      'GroupAddMembersModal_GroupUsersSearch',
    ],
    awaitRefetchQueries: true,
  });

  const userGroupPermissions = useGroupPermissions(groupId);

  const onClickRemove = async (): Promise<void> => {
    try {
      await deleteUserGroupLink({
        variables: {
          input: {
            filter: {
              userId: { _eq: member._id },
              groupId: { _eq: groupId },
            },
          },
        },
      });
      toast({ title: 'Removed member from group.', status: 'success' });
    } catch (err) {
      toast({
        title: 'Failed to remove member from group.',
        status: 'error',
      });
      logError(err);
    }
  };

  const onRoleSelected = async (role: EUserGroupRole): Promise<void> => {
    try {
      await updateUserGroupLink({
        variables: {
          input: {
            filter: {
              userId: { _eq: member._id },
              groupId: { _eq: groupId },
            },
            data: {
              userGroupRole: role,
            },
          },
        },
        refetchQueries: [
          'GroupMembers_GroupUsersSearch',
          'GroupAddMembersModal_GroupUsersSearch',
        ],
        awaitRefetchQueries: true,
      });
      toast({
        title: `Successfully changed member to ${prettyPrintEnum(role)}.`,
        status: 'success',
      });
    } catch (err) {
      toast({
        title: `Failed to change role to ${prettyPrintEnum(role)}.`,
        status: 'error',
      });
      logError(err);
    }
  };

  const groupMembership = member?.groupLinks?.find(
    (link) => link.groupId === groupId
  );

  return !groupMembership ? null : (
    <Tr>
      <Td maxWidth="1px" width="full">
        <Stack direction="row" whiteSpace="normal">
          <Avatar
            src={member.proxyProfilePictureUrl || undefined}
            name={member.displayName}
            boxSize={10}
            mr="5"
          />
          <Box flex="1">
            <Link as={NextLink} href={getUserLink(member._id)}>
              {/* {`${member.displayName}${
                member.nameTitle ? ', ' + member.nameTitle : ''
              }`} */}
              {member.displayName}
            </Link>
            <Text color="gray.600">
              {member.jobTitle ? member.jobTitle : null}
              {member.company && member.jobTitle ? ' at ' : ''}
              {member.company ? member.company : null}
            </Text>
          </Box>
        </Stack>
      </Td>
      <Td>
        {!userGroupPermissions.isLoading &&
        (userGroupPermissions.canEditMemberRoles ||
          userGroupPermissions.canEditMemberModeratorRole) ? (
          <GroupRoleSelect
            size="sm"
            width="36"
            value={
              groupMembership?.userGroupRole
                ? (groupMembership.userGroupRole as EUserGroupRole)
                : undefined
            }
            onChange={onRoleSelected}
            isDisabled={
              updateUserGroupLinkResult.loading ||
              deleteUserGroupLinkResult.loading
            }
          />
        ) : (
          <Text
            textStyle="compact"
            // styled to match the select buttons in the other rows
            px="3"
            border="1px"
            borderColor="transparent"
          >
            {groupMembership.userGroupRole
              ? READABLE_USER_ROLE_FOR_GROUP[
                  groupMembership.userGroupRole as EUserGroupRole
                ]
              : READABLE_USER_ROLE_FOR_GROUP[EUserGroupRole.GroupMember]}
          </Text>
        )}
      </Td>
      <Td>
        <Tag colorScheme="green" variant="subtle">
          Active
        </Tag>
      </Td>
      {!userGroupPermissions.isLoading &&
        userGroupPermissions.canEditMembership && (
          <Td>
            <IconButton
              icon={<XIcon />}
              onClick={onClickRemove}
              aria-label="Remove user"
              variant="ghost"
              colorScheme="gray"
            >
              Remove
            </IconButton>
          </Td>
        )}
    </Tr>
  );
};

Member.userFragment = gql`
  fragment GroupMembersPaneMember_UserFragment on User {
    _id
    displayName
    jobTitle
    company
    nameTitle
    proxyProfilePictureUrl(
      input: { w: 140, h: 140, fit: "facearea", facepad: 10.0 }
    )
    groupLinks {
      _id
      userId
      groupId
      userGroupRole
    }
  }
`;
